import { getDaysInMonth, startOfMonth, subMonths } from "date-fns";
import { deAT } from "date-fns/locale/de-AT";

import { formatMonthYear } from "../../../../format/formatDate";
import { toMonthKey } from "../../../../utils/monthKey";
import { roundToCents } from "../../../../utils/roundToCents";
import { AssetIncomeRequest, AssetIncomeResponse } from "../../../api/ClientFileApi";
import {
  INCOME_MONTH_COUNT,
  IncomeEntryFormValue,
  IncomeMonthFormValue,
  IncomeTypeTraits,
  SELECTABLE_MONTH_COUNT,
} from "./IncomeView.types";

const MONTH_KEY_PATTERN = /^(\d{4})-(\d{2})$/;

export function parseMonthKey(key: string): { year: number; month: number } | null {
  const match = MONTH_KEY_PATTERN.exec(key);
  return match ? { year: Number(match[1]), month: Number(match[2]) } : null;
}

function monthKeyToDate(key: string | null): Date | null {
  const parsed = key ? parseMonthKey(key) : null;
  return parsed ? new Date(parsed.year, parsed.month - 1, 1) : null;
}

export function formatMonthKey(key: string | null): string {
  const date = monthKeyToDate(key);
  return date ? formatMonthYear(date, deAT) : "";
}

export function daysInMonth(key: string | null): number | null {
  const date = monthKeyToDate(key);
  return date ? getDaysInMonth(date) : null;
}

export function selectableMonthKeys(today: Date, storedKeys: Array<string | null>): string[] {
  const rolling = Array.from({ length: SELECTABLE_MONTH_COUNT }, (_, offset) => {
    const date = subMonths(startOfMonth(today), offset);
    return toMonthKey(date.getFullYear(), date.getMonth() + 1);
  });

  const outside = storedKeys.filter((key): key is string => key !== null && !rolling.includes(key));

  return [...rolling, ...outside].sort((a, b) => b.localeCompare(a));
}

export function calculateMonthlyIncome(
  entry: IncomeEntryFormValue,
  month: IncomeMonthFormValue,
  isNonSeizable: boolean
): number | null {
  if (month.paidAmount === null) {
    return null;
  }

  const withSpecialPayments = entry.hasSpecialPayments === true;
  const garnished = !isNonSeizable && entry.isPartiallyGarnished === true ? (month.garnishedAmount ?? 0) : 0;
  const familyAllowance =
    withSpecialPayments && entry.hasEmployerFamilyAllowance === true ? (month.familyAllowance ?? 0) : 0;
  const settledExpenses =
    withSpecialPayments && entry.hasEmployerSettledExpenses === true ? (month.employerSettledExpenses ?? 0) : 0;
  const expenseAllowance =
    withSpecialPayments && entry.hasExpenseAllowances === true ? (month.expenseAllowance ?? 0) : 0;
  const benefitInKind = withSpecialPayments && entry.hasBenefitInKind === true ? (entry.benefitInKindAmount ?? 0) : 0;

  return roundToCents(
    month.paidAmount + garnished - familyAllowance + settledExpenses - expenseAllowance + benefitInKind
  );
}

export interface IncomeAverage {
  amount: number | null;
  usedMonths: IncomeMonthFormValue[];
}

export function calculateAverage(entry: IncomeEntryFormValue, isNonSeizable: boolean): IncomeAverage {
  const withSpecialPayments = entry.hasSpecialPayments === true;

  const used = entry.months
    .filter((month) => isMonthRecorded(month) && !(withSpecialPayments && month.specialPaymentPaid === true))
    .map((month) => ({ month, income: calculateMonthlyIncome(entry, month, isNonSeizable) }))
    .filter((x): x is { month: IncomeMonthFormValue; income: number } => x.income !== null && x.income >= 0);

  if (used.length === 0) {
    return { amount: null, usedMonths: [] };
  }

  const sum = used.reduce((total, x) => total + x.income, 0);
  return { amount: roundToCents(sum / used.length), usedMonths: used.map((x) => x.month) };
}

export function isMonthRecorded(month: IncomeMonthFormValue): boolean {
  return month.month !== null && month.paidAmount !== null;
}

export function hasDuplicateMonths(entry: IncomeEntryFormValue): boolean {
  const selected = entry.months.map((month) => month.month).filter((key): key is string => key !== null);
  return new Set(selected).size !== selected.length;
}

export function createEmptyMonth(): IncomeMonthFormValue {
  return {
    month: null,
    paidAmount: null,
    garnishedAmount: null,
    expenseAllowance: null,
    familyAllowance: null,
    employerSettledExpenses: null,
    specialPaymentPaid: null,
  };
}

export function createEmptyIncome(key: string): IncomeEntryFormValue {
  return {
    key,
    id: null,
    payerNameAndAddress: null,
    incomeType: null,
    hasSpecialPayments: null,
    specialPaymentCount: null,
    dailyRate: null,
    note: null,
    isPartiallyGarnished: null,
    hasEmployerFamilyAllowance: null,
    hasEmployerSettledExpenses: null,
    hasBenefitInKind: null,
    benefitInKindAmount: null,
    receivesTips: null,
    monthlyTipAmount: null,
    hasExpenseAllowances: null,
    isAverageRepresentative: true,
    nonRepresentativeReason: null,
    actualAverageIncome: null,
    months: Array.from({ length: INCOME_MONTH_COUNT }, createEmptyMonth),
  };
}

export function fromIncomeResponse(income: AssetIncomeResponse): IncomeEntryFormValue {
  return {
    key: income.id,
    id: income.id,
    payerNameAndAddress: income.payerNameAndAddress,
    incomeType: income.incomeType,
    hasSpecialPayments: income.hasSpecialPayments,
    specialPaymentCount: income.specialPaymentCount ?? null,
    dailyRate: income.dailyRate ?? null,
    note: income.note ?? null,
    isPartiallyGarnished: income.isPartiallyGarnished ?? null,
    hasEmployerFamilyAllowance: income.hasEmployerFamilyAllowance ?? null,
    hasEmployerSettledExpenses: income.hasEmployerSettledExpenses ?? null,
    hasBenefitInKind: income.hasBenefitInKind ?? null,
    benefitInKindAmount: income.benefitInKindAmount ?? null,
    receivesTips: income.receivesTips ?? null,
    monthlyTipAmount: income.monthlyTipAmount ?? null,
    hasExpenseAllowances: income.hasExpenseAllowances ?? null,
    isAverageRepresentative: income.isAverageRepresentative,
    nonRepresentativeReason: income.nonRepresentativeReason ?? null,
    actualAverageIncome: income.actualAverageIncome ?? null,
    months: income.months.map((month) => ({
      month: month.year !== null && month.month !== null ? toMonthKey(month.year, month.month) : null,
      paidAmount: month.paidAmount,
      garnishedAmount: month.garnishedAmount ?? null,
      expenseAllowance: month.expenseAllowance ?? null,
      familyAllowance: month.familyAllowance ?? null,
      employerSettledExpenses: month.employerSettledExpenses ?? null,
      specialPaymentPaid: month.specialPaymentPaid ?? null,
    })),
  };
}

export function toIncomeRequest(entry: IncomeEntryFormValue, traits: IncomeTypeTraits): AssetIncomeRequest {
  const { hasSpecialPayments, isAverageRepresentative } = entry;
  const withSpecialPayments = hasSpecialPayments === true;
  const isNotRepresentative = isAverageRepresentative === false;
  const isPartiallyGarnished = traits.isNonSeizable ? null : entry.isPartiallyGarnished;
  const answer = (value: boolean | null) => (withSpecialPayments ? value : null);

  return {
    id: entry.id,
    payerNameAndAddress: entry.payerNameAndAddress,
    incomeType: entry.incomeType,
    hasSpecialPayments,
    specialPaymentCount: withSpecialPayments ? entry.specialPaymentCount : null,
    dailyRate: traits.hasDailyRate ? entry.dailyRate : null,
    note: entry.note,
    isPartiallyGarnished,
    hasEmployerFamilyAllowance: answer(entry.hasEmployerFamilyAllowance),
    hasEmployerSettledExpenses: answer(entry.hasEmployerSettledExpenses),
    hasBenefitInKind: answer(entry.hasBenefitInKind),
    benefitInKindAmount: withSpecialPayments && entry.hasBenefitInKind === true ? entry.benefitInKindAmount : null,
    receivesTips: answer(entry.receivesTips),
    monthlyTipAmount: withSpecialPayments && entry.receivesTips === true ? entry.monthlyTipAmount : null,
    hasExpenseAllowances: answer(entry.hasExpenseAllowances),
    isAverageRepresentative,
    nonRepresentativeReason: isNotRepresentative ? entry.nonRepresentativeReason : null,
    actualAverageIncome: isNotRepresentative ? entry.actualAverageIncome : null,
    months: entry.months.map((month) => {
      const parsed = month.month ? parseMonthKey(month.month) : null;

      return {
        year: parsed?.year ?? null,
        month: parsed?.month ?? null,
        paidAmount: month.paidAmount,
        garnishedAmount: isPartiallyGarnished === true ? month.garnishedAmount : null,
        expenseAllowance: withSpecialPayments && entry.hasExpenseAllowances === true ? month.expenseAllowance : null,
        familyAllowance:
          withSpecialPayments && entry.hasEmployerFamilyAllowance === true ? month.familyAllowance : null,
        employerSettledExpenses:
          withSpecialPayments && entry.hasEmployerSettledExpenses === true ? month.employerSettledExpenses : null,
        specialPaymentPaid: withSpecialPayments ? month.specialPaymentPaid : null,
      };
    }),
  };
}

export function fillMonthsFromDailyRate(entry: IncomeEntryFormValue): IncomeMonthFormValue[] {
  if (entry.dailyRate === null) {
    return entry.months;
  }

  const dailyRate = entry.dailyRate;
  return entry.months.map((month) => {
    const days = daysInMonth(month.month);
    return days === null ? month : { ...month, paidAmount: roundToCents(dailyRate * days) };
  });
}
